import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { SERVICE_ITEM_CATEGORIES, type ServiceItemCategory } from '@wodi/domain';
import { Button, Checkbox, LoadingBlock, Notice, Section } from '@wodi/ui';
import { useState } from 'react';

import {
  billingKeys,
  billingSettingsQuery,
  saveBillingSettings,
  setServicePointChargeItem,
} from '../api/billing.ts';
import { can, sessionQuery } from '../api/session.ts';
import styles from '../billing/billing.module.css';
import { PaybillAddresses } from '../billing/paybill-addresses.tsx';
import { ServicePicker } from '../billing/service-picker.tsx';
import { CATEGORY_LABELS } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

/** What each pay-first setting means at the counter (plan §6.6). */
const GATE_SENTENCES: Partial<Record<ServiceItemCategory, string>> = {
  consultation: 'The consultation fee must be paid before the patient is seen.',
  lab_test: 'Lab tests must be paid before the sample is collected.',
  imaging: 'Imaging must be paid before the scan.',
  medicine: 'Medicines must be paid before they are dispensed.',
  procedure: 'Procedures must be paid before they are done.',
};

/**
 * Settings > Billing: what must be paid before the service, and the consultation fee each
 * consultation point charges at check-in (plan §13 decisions 1 and 16).
 */
export function BillingSettingsPage() {
  const settings = useQuery(billingSettingsQuery);
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayManage = session !== null && can(session, 'billing.settings.manage');
  return (
    <SettingsPage title="Billing">
      {settings.isPending ? <LoadingBlock label="Loading billing settings" rows={4} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void settings.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(settings.error, "Billing settings couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {settings.isSuccess ? (
        <>
          <PayBefore
            key={settings.data.version}
            payBefore={settings.data.payBefore}
            version={settings.data.version}
          />
          <ConsultationFees points={settings.data.consultationPoints} />
        </>
      ) : null}
      {mayManage ? <PaybillAddresses /> : null}
    </SettingsPage>
  );
}

function PayBefore({ payBefore, version }: { payBefore: string[]; version: number }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState(new Set(payBefore));
  const save = useMutation({
    mutationFn: () => saveBillingSettings([...chosen], version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.settings });
      toasts.show({ title: 'Billing settings saved' });
    },
  });
  const gated = SERVICE_ITEM_CATEGORIES.filter((c) => GATE_SENTENCES[c] !== undefined);
  return (
    <Section title="Pay before service">
      <p>Anything not ticked is paid at the end of the visit.</p>
      {gated.map((c) => (
        <Checkbox
          key={c}
          isSelected={chosen.has(c)}
          description={GATE_SENTENCES[c] ?? ''}
          onChange={(on) => {
            setChosen((set) => {
              const next = new Set(set);
              if (on) next.add(c);
              else next.delete(c);
              return next;
            });
          }}
        >
          {CATEGORY_LABELS[c]}
        </Checkbox>
      ))}
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The settings weren't saved.")}</Notice>
      ) : null}
      <div>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving"
          isDisabled={!online}
          onPress={() => {
            save.mutate();
          }}
        >
          Save
        </Button>
      </div>
    </Section>
  );
}

function ConsultationFees({
  points,
}: {
  points: {
    id: string;
    name: string;
    status: string;
    chargeItem: { id: string; code: string; name: string } | null;
  }[];
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: ({ pointId, itemId }: { pointId: string; itemId: string | null }) =>
      setServicePointChargeItem(pointId, itemId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.settings });
      setEditing(null);
      toasts.show({ title: 'Consultation fee changed' });
    },
  });
  return (
    <Section title="Consultation fee at check-in">
      <p>
        Checking a patient in to one of these points charges its fee. Leave it empty where
        consultation is free.
      </p>
      {points.length === 0 ? (
        <p>No consultation service points here yet. Add them in Service points.</p>
      ) : null}
      {points.map((p) => (
        <div key={p.id} className={styles.section}>
          <p className={styles.toolbar}>
            <strong>{p.name}</strong>
            <span>
              {p.chargeItem === null ? (
                'No fee'
              ) : (
                <>
                  <span className="wodi-id">{p.chargeItem.code}</span> {p.chargeItem.name}
                </>
              )}
            </span>
            {p.status === 'inactive' ? <span>Switched off</span> : null}
            <Button
              dense
              isDisabled={!online}
              onPress={() => {
                setEditing(editing === p.id ? null : p.id);
              }}
            >
              Change fee
            </Button>
            {p.chargeItem === null ? null : (
              <Button
                dense
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  set.mutate({ pointId: p.id, itemId: null });
                }}
              >
                Remove fee
              </Button>
            )}
          </p>
          {editing === p.id ? (
            <ServicePicker
              label={`Consultation fee for ${p.name}`}
              category="consultation"
              onSelect={(item) => {
                set.mutate({ pointId: p.id, itemId: item.id });
              }}
            />
          ) : null}
        </div>
      ))}
      {set.isError ? (
        <Notice tone="error">{errorText(set.error, "The fee wasn't changed. Try again.")}</Notice>
      ) : null}
    </Section>
  );
}
